import { Inject, Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { ActionUpdatePayload, ConfType, DriverNoteDeletePayload, DriverNoteUpsertPayload, Machine } from '@models/*';
import { Store } from '@ngrx/store';
import * as appStore from '@appstore/*';
import * as overviewStore from '../../overview/store';
import { RestService } from '../../core/services/rest.service';
import { ToastService } from '../../core/services/toast.service';
import { ErrorService } from '../../core/services/error.service';
import { NetworkService } from '../../core/services/network.service';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG, AppConfig } from '../../config/app-config';
import { ZenithToastService } from '../../components/zenith/zenith-toast/zenith-toast.service';
import { Icon, ZenithAppearance } from '../../components/zenith/zenith-enums';

@Injectable({
  providedIn: 'root'
})
export class ActionService extends RestService {
  constructor(
    @Inject(APP_CONFIG) appConfig: AppConfig,
    private translate: TranslateService,
    private zenithToastService: ZenithToastService,
    private toastService: ToastService,
    http: HttpClient,
    errorService: ErrorService,
    networkService: NetworkService,
    private store: Store<overviewStore.OverviewState | appStore.AppState>,
    injector: Injector,
  ) {
    super(appConfig, http, errorService, networkService, injector);
  }

    completeTourStopActions(payload: ActionUpdatePayload[], confirmationType: ConfType): Observable<boolean> {
        return this.post(`${this.ACTION_UPDATES_URL}`, payload).pipe(
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                    this.toastService.generateSuccessToast(confirmationType);
                    this.store.dispatch(new appStore.RouterNavigateToTourStop);
                }
            }),
            map(res => (res instanceof HttpErrorResponse || res.error) ? false : true)
        );
    }

  fuelRefill(payload: {tourStopActionId: number, transportId: number, amount: number}): Observable<boolean> {
        return this.post(`${this.FUEL_REFILL_URL}`, payload).pipe(
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                  this.zenithToastService.showToast(this.translate.instant('toast.success.fuel_refill'), ZenithAppearance.SUCCESS, Icon.ADD);
                }
            }),
            map(res => (res instanceof HttpErrorResponse || res.error) ? false : true)
        );
    }

    searchMachine(fleetNumber: string): Observable<Machine> {
        return this.post(
            `${this.SEARCH_MACHINE_URL}`,
            { fleetNumber },
            null,
            true
        ).pipe(
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                    this.toastService.generateSuccessToast(ConfType.SEARCH_MACHINE, { fleetNumber });
                } else {
                    this.toastService.generateFailureToast(ConfType.SEARCH_MACHINE, { fleetNumber });
                }
            }),
            map(res => (res instanceof HttpErrorResponse || res.error) ? null : res.data)
        );
    }

    switchMachine({tourStopActionId, contractDetailId, newMachineInternalId, oldFleetNumber, newFleetNumber, noAutoSwap }): Observable<boolean | any>{
        return this.post(
            `${this.SWITCH_MACHINE_URL}`,
            {tourStopActionId, contractDetailId, machineInternalId: newMachineInternalId, noAutoSwap },
            null,
            true
        ).pipe(
            tap(res => {
                if (res && res.data && res.data.status === 'OK') {
                    this.toastService.generateSuccessToast(ConfType.SWITCH_MACHINE, { oldFleetNumber, newFleetNumber });
                } else if (res && !(res.data && res.data.status === 'PROPOSAL')) {
                    this.toastService.generateFailureToast(ConfType.SWITCH_MACHINE);
                }
            }),
            map(res => (res instanceof HttpErrorResponse || res.error) ? false : res.data)
        );
    }

    upsertDriverNote(payload: DriverNoteUpsertPayload): Observable<any> {
        return this.post(`${this.UPSERT_DRIVER_NOTE}`, payload).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? false : res.data)
        );
    }

    deleteDriverNote(payload: DriverNoteDeletePayload): Observable<boolean> {
        return this.post(`${this.DELETE_DRIVER_NOTE}`, payload).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? false : true)
        );
    }
}
